import { memo, useEffect, useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { MultipleVersionTextarea } from "~/components/ui/multiple-version-textarea";
import { TrashIcon } from "~/icons/TrashIcon";
import { cn } from "~/lib/utils";
import iconAirport from "~/assets/distances/airport.png";
import iconCityCenter from "~/assets/distances/city-center.png";
import iconRailstation from "~/assets/distances/railstation.png";
import iconMotorway from "~/assets/distances/motorway.png";
import iconPublicTransport from "~/assets/distances/public-transport.png";
import type { ReactNode } from "react";
import { MultipleVersionInput } from "~/components/ui/multiple-version-input";
import { PriceInput } from "~/components/ui/price-input";
import { TranslationContent } from "~/components/TranslationContent";
import { Checkbox } from "~/components/ui/checkbox";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { NumberInput } from "~/components/ui/number-input";

export const DistanceItem = memo(
  ({
    distance,
    onUpdate,
    onRemove,
  }: {
    distance: {
      id: string;
      name: string;
      abbreviation: string;
      type:
      | "motorway"
      | "railstation"
      | "airport"
      | "city-center"
      | "public-transport";
      distance: number | undefined;
      unit: "m" | "km";
      sorting?: number;
    };
    onUpdate: (updates: Partial<typeof distance>) => void;
    onRemove: () => void;
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: distance.id });

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString({ x: 0, y: transform?.y ?? 0, scaleX: transform?.scaleX ?? 1, scaleY: transform?.scaleY ?? 1 }),
      transition,
      opacity: isDragging ? 0.7 : 1,
    };

    // Use local state for immediate UI updates, sync to form on blur
    const [localDistance, setLocalDistance] = useState(distance);

    // Sync local state when prop changes (from external updates)
    useEffect(() => {
      setLocalDistance(distance);
    }, [
      distance.id,
      distance.name,
      distance.abbreviation,
      distance.type,
      distance.distance,
      (distance as any).unit,
    ]);

    const handleNameChange = (value: string) => {
      setLocalDistance((prev) => ({ ...prev, name: value }));
    };

    const handleNameBlur = () => {
      if (localDistance.name !== distance.name) {
        onUpdate({ name: localDistance.name });
      }
    };

    const handleAbbreviationChange = (value: string) => {
      setLocalDistance((prev) => ({ ...prev, abbreviation: value }));
    };

    const handleAbbreviationBlur = () => {
      if (localDistance.abbreviation !== distance.abbreviation) {
        onUpdate({ abbreviation: localDistance.abbreviation });
      }
    };

    const handleDistanceChange = (value: number | undefined) => {
      const next =
        localDistance.unit === "m"
          ? value != null
            ? Math.floor(Math.max(0, value))
            : undefined
          : value != null
            ? Math.max(0, value)
            : undefined;

      setLocalDistance((prev) => ({
        ...prev,
        distance: next,
      }));
    };

    const handleDistanceBlur = (value: number | undefined) => {
      const finalValue =
        typeof localDistance.distance === "number" ? localDistance.distance : 0;

      setLocalDistance((prev) => ({
        ...prev,
        distance: finalValue,
      }));

      onUpdate({ distance: finalValue });
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        className="w-full space-y-3"
      >
        <div className="grid w-full gap-3 sm:grid-cols-[minmax(160px,180px)_minmax(180px,1fr)_minmax(90px,110px)]">
          <Select
            value={localDistance.type}
            onValueChange={(value: string) => {
              const typedValue = value as
                | "motorway"
                | "railstation"
                | "airport"
                | "city-center"
                | "public-transport";
              setLocalDistance(
                (prev) => ({ ...prev, type: typedValue }) as typeof prev,
              );
              onUpdate({ type: typedValue });
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="motorway">
                <img src={iconMotorway} alt="" />
                Autobahn
              </SelectItem>
              <SelectItem value="railstation">
                <img src={iconRailstation} alt="" />
                Bahnhof
              </SelectItem>
              <SelectItem value="airport">
                <img src={iconAirport} alt="" />
                Flughafen
              </SelectItem>
              <SelectItem value="city-center">
                <img src={iconCityCenter} alt="" />
                Stadtzentrum
              </SelectItem>
              <SelectItem value="public-transport">
                <img src={iconPublicTransport} alt="" />
                ÖPNV
              </SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="text"
            placeholder="Name"
            value={localDistance.name}
            onChange={(e) => handleNameChange(e.target.value)}
            onBlur={handleNameBlur}
            className="w-full"
          />
          <Input
            type="text"
            placeholder="Abkürzung"
            value={localDistance.abbreviation}
            onChange={(e) => handleAbbreviationChange(e.target.value)}
            onBlur={handleAbbreviationBlur}
            className="w-full"
          />
        </div>

        <div className="flex w-full flex-wrap items-center gap-3">
          <div className="flex w-full items-center sm:min-w-[240px] sm:flex-1">
            <NumberInput
              value={
                localDistance.distance !== undefined &&
                localDistance.distance !== null
                  ? localDistance.unit === "m"
                    ? Math.floor(localDistance.distance)
                    : localDistance.distance
                  : undefined
              }
              onChange={handleDistanceChange}
              onBlur={handleDistanceBlur}
              allowDecimals={localDistance.unit === "km"}
              allowEmpty
              className="w-full [&>input]:rounded-r-none"
            />
            <Select
              value={localDistance.unit}
              onValueChange={(value: string) => {
                const nextUnit = value === "m" ? "m" : "km";
                const prevUnit = localDistance.unit;
                const prevDistance = localDistance.distance ?? 0;
                let convertedDistance = prevDistance;

                if (prevUnit !== nextUnit) {
                  if (prevUnit === "km" && nextUnit === "m") {
                    // Beispiel: 12,5 km -> 12 m
                    convertedDistance = Math.floor(prevDistance);
                  } else if (prevUnit === "m" && nextUnit === "km") {
                    // Beispiel: 12 m -> 12 km (Zahl bleibt gleich)
                    convertedDistance = prevDistance;
                  }
                }

                setLocalDistance((prev) => ({
                  ...prev,
                  unit: nextUnit,
                  distance: convertedDistance,
                }));

                onUpdate({
                  unit: nextUnit as any,
                  distance: convertedDistance,
                });
              }}
            >
              <SelectTrigger className="bg-gray-darker text-white rounded-l-none -ml-[2px] icon-white w-[69px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="km">km</SelectItem>
                <SelectItem value="m">m</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 shrink-0 sm:ml-auto">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="cursor-grab"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="flatDark"
              size="icon"
              className="shrink-0"
              onClick={onRemove}
            >
              <TrashIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if the distance data actually changed
    return (
      prevProps.distance.id === nextProps.distance.id &&
      prevProps.distance.name === nextProps.distance.name &&
      prevProps.distance.abbreviation === nextProps.distance.abbreviation &&
      prevProps.distance.type === nextProps.distance.type &&
      prevProps.distance.distance === nextProps.distance.distance
    );
  },
);
DistanceItem.displayName = "DistanceItem";

export const MemoizedInput = memo(
  ({
    value,
    index,
    onUpdate,
  }: {
    value: string[];
    index: number;
    onUpdate: (value: string[]) => void;
  }) => {
    // Use local state for immediate UI updates
    const [localValue, setLocalValue] = useState(value);

    // Sync local state when prop changes (from external updates)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (newValue: string[]) => {
      setLocalValue(newValue);
      // Update form with optimized options (doesn't trigger room array watch)
      onUpdate(newValue);
    };

    const handleBlur = () => {
      // Ensure final value is synced
      if (localValue !== value) {
        onUpdate(localValue);
      }
    };

    return (
      <MultipleVersionInput
        name={["text-german", "text-english"]}
        value={localValue}
        label={["Deutsch", "Englisch"]}
        onChange={(v) => handleChange(v)}
        onBlur={handleBlur}
        maxChars={40}
      />
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if value or index actually changed
    return (
      prevProps.value === nextProps.value && prevProps.index === nextProps.index
    );
  },
);
MemoizedInput.displayName = "MemoizedInput";

export const MemoizedTextarea = memo(
  ({
    value,
    index,
    onUpdate,
  }: {
    value: string[];
    index: number;
    onUpdate: (value: string[]) => void;
  }) => {
    // Use local state for immediate UI updates
    const [localValue, setLocalValue] = useState(value);

    // Sync local state when prop changes (from external updates)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = (newValue: string[]) => {
      setLocalValue(newValue);
      // Update form with optimized options (doesn't trigger room array watch)
      onUpdate(newValue);
    };

    const handleBlur = () => {
      // Ensure final value is synced
      if (localValue !== value) {
        onUpdate(localValue);
      }
    };

    return (
      <MultipleVersionTextarea
        name={["text-german", "text-english"]}
        value={localValue}
        label={["Deutsch", "Englisch"]}
        onChange={(v) => handleChange(v)}
        onBlur={handleBlur}
        maxChars={40}
      />
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if value or index actually changed
    return (
      prevProps.value === nextProps.value && prevProps.index === nextProps.index
    );
  },
);
MemoizedTextarea.displayName = "MemoizedTextarea";

export function PricingSection({
  name,
  price,
  priceMode,
  vatAmount,
  currency,
  metaValue,
  metaLabel,
  metaUnit,
  onPriceChange,
  onCurrencyChange,
  onMetaChange,
  enableOptionalMode = false,
  enableUnlimitedMode = false,
  showMetaInput = true,
  showLabelBanner = true,
  notOfferedOption = "Nicht angeboten",
  priceAndMetaOption = "Euro-Betrag eingeben",
  priceOnlyOption

}: {
  name: string;
  price: number;
  priceMode: "gross" | "net";
  vatAmount: number;
  currency: "EUR" | "CHF" | "USD";
  metaValue?: number;
  metaLabel?: string;
  metaUnit?: string;
  onPriceChange: (price: number) => void;
  onCurrencyChange: (currency: "EUR" | "CHF" | "USD") => void;
  onMetaChange?: (metaValue: number) => void;
  enableOptionalMode?: boolean;
  enableUnlimitedMode?: boolean;
  showMetaInput?: boolean;
  showLabelBanner?: boolean;
  notOfferedOption?: string;
  priceAndMetaOption?: string;
  priceOnlyOption?: string;
}) {
  const isNotOffered = price === -1;
  // priceAndMetaOption: price !== -1 && metaValue !== -1
  // priceOnlyOption: price !== -1 && metaValue === -1
  const isPriceAndMeta =
    !isNotOffered &&
    metaValue !== -1 &&
    metaValue !== undefined &&
    metaValue !== null;
  const isPriceOnly =
    !isNotOffered &&
    (metaValue === -1 || metaValue === undefined || metaValue === null);

  const mode = isNotOffered
    ? "not-offered"
    : isPriceAndMeta
      ? "priceAndMeta"
      : "priceOnly";

  const handleModeChange = (value: string) => {
    if (value === "not-offered") {
      onPriceChange(-1);
      // Reset meta value when switching to "not offered"
      onMetaChange?.(0);
    } else if (value === "priceAndMeta") {
      // Switch to priceAndMeta - set price to 0 if it was -1, set metaValue to 0 if it was -1
      if (price === -1) {
        onPriceChange(0);
      }
      if (metaValue === -1 || metaValue === undefined || metaValue === null) {
        onMetaChange?.(0);
      }
    } else if (value === "priceOnly") {
      // Switch to priceOnly - set price to 0 if it was -1, set metaValue to -1
      if (price === -1) {
        onPriceChange(0);
      }
      onMetaChange?.(-1);
    }
  };

  // Wenn priceOnly aktiv ist, Meta-Input ausblenden
  const shouldShowMetaInput = showMetaInput && metaLabel && !isPriceOnly;

  return (
    <div className="flex flex-col items-start w-full">
      {showLabelBanner && (
        <div className="bg-black text-white px-5 py-2.5 text-lg w-full">
          {name}
        </div>
      )}
      {enableOptionalMode && (
        <div className="mt-3 w-full">
          <Select value={mode} onValueChange={handleModeChange}>
            <SelectTrigger className="w-full lg:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-offered">{notOfferedOption}</SelectItem>
              <SelectItem value="priceAndMeta">{priceAndMetaOption}</SelectItem>
              {priceOnlyOption && (
                <SelectItem value="priceOnly">{priceOnlyOption}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
      {(!enableOptionalMode || !isNotOffered) && (
        <div
          className={cn(
            "mt-3 flex lg:flex-row flex-col gap-6 lg:gap-12 w-full",
            !shouldShowMetaInput && "lg:flex-col",
          )}
        >
          <div>
            <Label className="text-base" htmlFor={`price-${name}`}>
              Ab
            </Label>
            <div className="flex gap-3">
              <PriceInput
                value={price}
                onChange={onPriceChange}
                id={`price-${name}`}
                name={`price-${name}`}
                className="w-full"
              />
              {/* <Input
                type="text"
                value={price}
                id={`price-${name}`}
                onChange={(e) => onPriceChange?.(Number(e.target.value))}
              /> */}
              <Select
                value={currency || undefined}
                onValueChange={onCurrencyChange}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR</SelectItem>
                  {/* <SelectItem value="CHF">CHF</SelectItem>
                  <SelectItem value="USD">USD</SelectItem> */}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-1 text-neutral-dark">
              {priceMode === "gross" ? "Brutto Preis" : "Netto Preis"}
            </div>
          </div>
          {shouldShowMetaInput && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-base" htmlFor={`meta-${name}`}>
                  {metaLabel}
                </Label>
                {enableUnlimitedMode && (
                  <div className="flex gap-2 items-center">
                    <Checkbox
                      id={`unlimited-${name}`}
                      checked={metaValue === 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onMetaChange?.(0); // 0 = unbeschränkt
                        } else {
                          onMetaChange?.(24); // Default: 24 Stunden
                        }
                      }}
                    />
                    <Label
                      htmlFor={`unlimited-${name}`}
                      className="text-sm font-normal cursor-pointer"
                    >
                      Unbeschränkt
                    </Label>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Input
                  type="text"
                  value={metaValue ?? ""}
                  id={`meta-${name}`}
                  disabled={enableUnlimitedMode && metaValue === 0}
                  onChange={(e) => onMetaChange?.(Number(e.target.value))}
                />
                {metaUnit && (
                  <Input
                    type="text"
                    value={metaUnit}
                    readOnly
                    className="w-20"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const IconSelect = ({
  value,
  onValueChange,
  items,
  className,
}: {
  value: string;
  onValueChange: (value: string) => void;
  items: Array<{
    id: string;
    label: string;
    icon: React.ReactElement;
  }>;
  className?: string;
}) => {
  return (
    <div className={cn("grid grid-cols-2 lg:flex gap-4", className)}>
      {items.map((item) => (
        <button
          type="button"
          key={item.id}
          className={cn(
            "cursor-pointer w-[74px] h-[54px] rounded-md",
            value === item.id
              ? "bg-primary text-gray-darker hover:bg-lemon-light"
              : "bg-gray-darker text-white hover:bg-gray-dark",
          )}
          onClick={() => {
            onValueChange(item.id);
          }}
        >
          {item.icon}
        </button>
      ))}
    </div>
  );
};

export const SectionIndicator = ({
  id,
  title,
  done,
  active,
  mandatory = false,
  hasUnsavedChanges = false,
  onSelect,
}: {
  id: string;
  title: string;
  done: boolean;
  active?: boolean;
  mandatory?: boolean;
  hasUnsavedChanges?: boolean;
  onSelect?: (id: string) => void;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (onSelect) {
      onSelect(id);
    }

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <a
      href={`#${id}`}
      onClick={handleClick}
      className={cn(
        "text-lg flex items-center gap-4 cursor-pointer transition-opacity rounded-md p-2",
        // done ? "opacity-100" : "opacity-50",
        active ? "font-medium" : "",
        mandatory && "bg-lemon-lightest border border-[#efe9ca]",
        // active ? "underline underline-offset-4" : "hover:opacity-100"
      )}
    >
      <div className="relative">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          className="size-7"
        >
          <rect
            width="24"
            height="24"
            fill={active ? "#4c4c4c" : done ? "#000" : "#9a9a9a"}
            rx="6"
          />
          <path
            fill={active ? "#fff8cf" : done ? "#ffde10" : "#f2f2f2"}
            stroke={active ? "#fff8cf" : done ? "#ffde10" : "#f2f2f2"}
            strokeWidth="1.5"
            d="m10.11 16.207 8.593-8.594-.811-.816-7.777 7.783-4.012-4.007-.81.81zm-.905.93-4.837-4.838a1.2 1.2 0 0 1-.28-.429 1.35 1.35 0 0 1 0-.959q.09-.236.28-.428l.828-.828q.2-.201.436-.288t.476-.087q.24 0 .476.087.235.087.43.28l3.096 3.098 6.877-6.877q.192-.194.43-.28.237-.088.478-.088.242 0 .476.087t.434.278l.828.834q.197.201.282.438.085.236.085.476t-.089.476-.279.43l-8.616 8.618q-.196.193-.43.278a1.4 1.4 0 0 1-.951 0q-.235-.086-.43-.278Z"
          />
        </svg>
        {hasUnsavedChanges && (
          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" />
        )}
      </div>
      <div className="w-full flex items-center justify-between gap-2">
        {title}
        {mandatory && (
          <span className="text-xs font-medium bg-lemon-light px-1.5 py-0.5 rounded">
            Pflicht
          </span>
        )}
      </div>
    </a>
  );
};

export const FormSection = ({
  id,
  children,
  tipTitle,
  tip,
}: {
  id: string;
  children: ReactNode;
  tipTitle?: string;
  tip?: ReactNode;
}) => {
  return (
    <div id={id} className="flex flex-col lg:flex-row gap-6 lg:gap-24 w-full">
      <div className="flex-1 order-2 lg:order-1">{children}</div>
      <div className="lg:w-[300px] shrink-0 order-1 lg:order-2">
        {tipTitle && tip && (
          <div className="lg:max-w-[250px] leading-relaxed text-neutral-darker">
            {tipTitle && (
              <div className="flex gap-2 items-center font-semibold mb-2 text-[#6a6a6a]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  className="size-6"
                >
                  <rect width="24" height="24" fill="currentColor" rx="6" />
                  <path
                    fill="#fff"
                    d="M12 17.85q-.579 0-.982-.412a1.39 1.39 0 0 1-.404-1.01h2.772q0 .6-.404 1.01-.403.412-.982.412m-2.204-2.479a.512.512 0 0 1-.524-.526q0-.222.15-.372.152-.15.374-.15h4.412q.22 0 .37.153t.15.374q0 .223-.15.372-.15.15-.37.149zm-.44-2.088a5.7 5.7 0 0 1-1.79-1.833Q6.9 10.35 6.9 8.966q0-2.071 1.512-3.586Q9.924 3.867 12 3.867t3.59 1.513 1.515 3.586q0 1.383-.658 2.483a5.5 5.5 0 0 1-1.801 1.834zm.411-1.136h4.478a3.8 3.8 0 0 0 1.258-1.369q.462-.84.461-1.81 0-1.636-1.165-2.799T12 5.006q-1.634 0-2.8 1.163-1.164 1.164-1.164 2.798 0 .97.463 1.81a3.8 3.8 0 0 0 1.268 1.37"
                  />
                </svg>
                <TranslationContent content={tipTitle} as="span" />
              </div>
            )}
            <div className="mt-2">{tip}</div>
          </div>
        )}
      </div>
    </div>
  );
};
